/**
 * This editor's kits, as the AI gateway serves them for its `editor` profile
 * (`GET /v1/agent/kits`): which tools a session declares, and what the model
 * reads about each (the MCP server describes its tools with it). This editor's
 * own tools hold only their schemas and code (`tools/editor-tools.ts`); a kit's
 * tool this host has no executor for is not declared.
 *
 * Every kit is on until settings switch it off, for this page's life.
 */

import type { ToolSpec } from './tools/types';

export const EDITOR_PROFILE = 'editor';

/** What the model reads about a tool: what it is for, and each input field's text by path. */
export interface ToolText {
    description: string;
    fields: Record<string, string | null>;
}

export interface Kit {
    name: string;
    tools: string[];
}

export interface KitManifest {
    profile: string;
    /** In the profile's order. */
    kits: Kit[];
    tools: Record<string, ToolText>;
    /** What `read_guide` returns, by guide name (`tools/guide-tools.ts`). */
    guides: Record<string, string>;
    /** Content hash of all the text the manifest names. */
    version: string;
}

let manifest: KitManifest | null = null;
const switchedOff = new Set<string>();
let version = 0;
const listeners = new Set<() => void>();

function changed(): void {
    version++;
    for (const listener of listeners) listener();
}

/** Bumped whenever the manifest or a toggle changes; tool caches key on it. */
export function kitsVersion(): number {
    return version;
}

export function onKitsChange(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getKitManifest(): KitManifest | null {
    return manifest;
}

export function setKitManifest(next: KitManifest): void {
    // A turn reads the same manifest again most of the time; the caches it keys stay.
    if (manifest?.version === next.version) return;
    manifest = next;
    changed();
}

export function isKitOn(name: string): boolean {
    return !switchedOff.has(name);
}

export function setKitOn(name: string, on: boolean): void {
    if (on === isKitOn(name)) return;
    if (on) switchedOff.delete(name);
    else switchedOff.add(name);
    changed();
}

function loaded(): KitManifest {
    if (!manifest) throw new Error('The assistant\'s kits are not loaded from the AI gateway.');
    return manifest;
}

const onKits = (m: KitManifest): Kit[] => m.kits.filter(kit => isKitOn(kit.name));

/** The tools the session offers, in kit order. */
export function offeredToolNames(): string[] {
    return onKits(loaded()).flatMap(kit => kit.tools);
}

/** Whether a kit that is on lists the tool. False until the kits are loaded. */
export function isToolOffered(name: string): boolean {
    return manifest !== null && onKits(manifest).some(kit => kit.tools.includes(name));
}

/**
 * `schema` with each field text set at its path: `a.b` is a property of object
 * field `a`, `a[].b` a property of array field `a`'s items. Returns the paths
 * the schema lacks, whose text is left out.
 */
export function describeSchema<T>(schema: T, fields: ToolText['fields']): { schema: T; missing: string[] } {
    type Node = { [key: string]: unknown };
    // A JSON copy, not structuredClone: the viewer's schemas share one object between fields (position, rotation and
    // scale are one vec3), and a clone that kept the sharing would give them all the last description set.
    const copy = JSON.parse(JSON.stringify(schema)) as T;
    const missing: string[] = [];
    for (const [path, text] of Object.entries(fields)) {
        if (!text) continue;
        let node: Node | undefined = copy as unknown as Node;
        for (const segment of path.split('.')) {
            const items = segment.endsWith('[]');
            const next: Node | undefined = (node?.properties as Record<string, Node> | undefined)?.[items ? segment.slice(0, -2) : segment];
            node = items ? next?.items as Node | undefined : next;
        }
        if (node) node.description = text;
        else missing.push(path);
    }
    return { schema: copy, missing };
}

/** `tool` with its kit text; text for a field its schema lacks is left out, and named on the console. */
export function describeTool(tool: ToolSpec): ToolSpec {
    const text = loaded().tools[tool.name];
    if (!text) return tool;
    const { schema, missing } = describeSchema(tool.input_schema, text.fields);
    if (missing.length) console.warn(`[kits] ${tool.name}: the gateway describes fields this editor's schema lacks: ${missing.join(', ')}`);
    return { ...tool, description: text.description, input_schema: schema };
}

/** How long one read may take before the load fails: the gateway answers these from memory. */
const LOAD_TIMEOUT_MS = 20_000;

/** Load the editor's kits from the gateway at `base`. */
export async function loadKits(base: string, headers: Record<string, string>): Promise<void> {
    const path = `/v1/agent/kits?profile=${EDITOR_PROFILE}`;
    const res = await fetch(`${base}${path}`, { headers, signal: AbortSignal.timeout(LOAD_TIMEOUT_MS) });
    if (!res.ok) throw new Error(`The AI gateway answered ${res.status} for ${path}.`);
    setKitManifest(await res.json() as KitManifest);
}

/**
 * Load the kits for an MCP server from the gateway `TRACE_AI_GATEWAY_URL`
 * names, as the service account whose key is `TRACE_AI_GATEWAY_KEY` (or with
 * a CMS access token there).
 */
export async function loadMcpKits(env: Record<string, string | undefined>): Promise<void> {
    const url = env.TRACE_AI_GATEWAY_URL?.trim().replace(/\/+$/, '');
    const key = env.TRACE_AI_GATEWAY_KEY?.trim();
    if (!url || !key) throw new Error('The MCP server reads its tool text from the AI gateway: set TRACE_AI_GATEWAY_URL and TRACE_AI_GATEWAY_KEY.');
    await loadKits(url, { Authorization: key.startsWith('trace_sk_') ? key : `Bearer ${key}` });
}
